/**
 * ============================================================================
 * Iron Meridian - Combat Log Component
 * ============================================================================
 * 
 * Displays combat results and turn events with expandable details.
 * Premium design with animated entries and clear outcome indicators.
 */

import { useState } from 'react'

export default function CombatLog({ entries, countries }) {
  const [expandedEntry, setExpandedEntry] = useState(null)

  if (!entries || entries.length === 0) {
    return (
      <div className="p-5">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <span>📜</span>
          Battle History
        </h2>
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-war-room-700/30 flex items-center justify-center">
            <span className="text-3xl opacity-20">⚔️</span>
          </div>
          <p className="text-sm text-gray-500">No battles yet</p>
          <p className="text-xs text-gray-600 mt-1">Combat results will appear here</p>
        </div>
      </div>
    )
  }

  const totalEvents = entries.reduce((acc, e) => acc + (e.events?.length || 0), 0)

  return (
    <div className="p-5">
      <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
        <span>📜</span>
        Battle History
        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
          {totalEvents} battle{totalEvents !== 1 ? 's' : ''}
        </span>
      </h2>

      <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
        {entries.map((entry, index) => (
          <TurnEntry
            key={entry.turn || index}
            entry={entry}
            countries={countries}
            isExpanded={expandedEntry === index}
            onToggle={() => setExpandedEntry(expandedEntry === index ? null : index)}
          />
        ))}
      </div>
    </div>
  )
}

function TurnEntry({ entry, countries, isExpanded, onToggle }) {
  const hasEvents = entry.events && entry.events.length > 0
  const victories = entry.events?.filter(e => e.outcome === 'victory' || e.winner === e.attacker).length || 0
  const defeats = (entry.events?.length || 0) - victories

  return (
    <div className="bg-war-room-800/60 rounded-xl overflow-hidden border border-war-room-600/30">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-war-room-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-war-room-700 flex items-center justify-center">
            <span className="text-war-room-accent font-bold text-lg">{entry.turn}</span>
          </div>
          <div className="text-left">
            <div className="font-medium text-sm text-white">Turn {entry.turn}</div>
            {hasEvents && (
              <div className="flex items-center gap-2 text-xs">
                {victories > 0 && <span className="text-green-400">✓ {victories} won</span>}
                {defeats > 0 && <span className="text-red-400">✕ {defeats} lost</span>}
              </div>
            )}
          </div>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 animate-fade-in">
          {hasEvents ? (
            entry.events.map((event, i) => (
              <CombatEvent key={i} event={event} countries={countries} />
            ))
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              No combat this turn
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CombatEvent({ event, countries }) {
  const attackerName = countries[event.attacker]?.name || event.attacker
  const defenderName = countries[event.defender]?.name || event.defender
  const attackerColor = countries[event.attacker]?.color || '#64748b'
  const defenderColor = countries[event.defender]?.color || '#64748b'
  const isVictory = event.outcome === 'victory' || event.winner === event.attacker

  return (
    <div className={`combat-entry rounded-xl p-4 ${isVictory ? 'victory' : 'defeat'}`}>
      {/* Combatants */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shadow-sm ring-1 ring-white/10"
            style={{ backgroundColor: attackerColor }}
          >
            {event.attacker?.slice(0, 2)}
          </div>
          <div>
            <div className="text-sm font-medium">{attackerName}</div>
            <div className="text-xs text-gray-500">Attacker</div>
          </div>
        </div>
        
        <div className="flex flex-col items-center px-4">
          <span className="text-2xl">⚔️</span>
          <span className="text-xs text-gray-500">vs</span>
        </div>
        
        <div className="flex items-center gap-2">
          <div>
            <div className="text-sm font-medium text-right">{defenderName}</div>
            <div className="text-xs text-gray-500 text-right">Defender</div>
          </div>
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shadow-sm ring-1 ring-white/10"
            style={{ backgroundColor: defenderColor }}
          >
            {event.defender?.slice(0, 2)}
          </div>
        </div>
      </div>

      {/* Outcome Badge */}
      <div className={`flex items-center justify-center gap-2 py-2.5 rounded-lg ${isVictory ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
        <span className={`font-bold ${isVictory ? 'text-green-400' : 'text-red-400'}`}>
          {isVictory ? '🏆 Territory Conquered' : '🛡️ Defense Held'}
        </span>
      </div>

      {/* Combat Stats */}
      {(event.attackerStrength || event.defenderStrength) && (
        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-war-room-600/30">
          <div className="bg-war-room-700/40 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500">Attack Power</div>
            <div className="font-bold text-blue-400">{event.attackerStrength?.toFixed(0) || '—'}</div>
          </div>
          <div className="bg-war-room-700/40 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500">Defense Power</div>
            <div className="font-bold text-red-400">{event.defenderStrength?.toFixed(0) || '—'}</div>
          </div>
          {event.attackerLosses !== undefined && (
            <div className="bg-war-room-700/40 rounded-lg p-2 text-center">
              <div className="text-xs text-gray-500">Attacker Losses</div>
              <div className="font-bold text-red-300">-{event.attackerLosses}</div>
            </div>
          )}
          {event.defenderLosses !== undefined && (
            <div className="bg-war-room-700/40 rounded-lg p-2 text-center">
              <div className="text-xs text-gray-500">Defender Losses</div>
              <div className="font-bold text-red-300">-{event.defenderLosses}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
